import { FilteringProperty } from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
    columnDefinitionToOption,
    configPropsToColumnDefinitions,
    configPropsToFilterProps,
    formatDecimal,
    NUMBER_FIELD_OPERATORS,
    TableContentConfigProps,
    TEXT_FIELD_OPERATORS
} from "../tableConfig";
import CopyTextField from "../../components/offline-maps/CopyTextField";
import {CollectionPreferencesProps} from "@amzn/awsui-components-react";

export interface StationSimulationData {
    mapPackSummaryId: string;
    stationId: string;
    avgTransitTimeDelta: number;
    currentAvgTransitTime: number;
    currentDpph: number;
    currentPackageCount: number;
    currentRouteCount: number;
    currentSpr: number;
    currentStopCount: number;
    currentTransitTime: number;
    dpphDelta: number;
    dpph_breach: number;
    nonNurserySprDelta: number;
    originalAvgTransitTime: number;
    originalDpph: number;
    originalPackageCount: number;
    originalRouteCount: number;
    originalSpr: number;
    originalStopCount: number;
    originalTransitTime: number;
    packageCountDelta: number;
    routeCountDelta: number;
    spr_breach: number;
    stopCountDelta: number;
    transitTimeDelta: number;
}

const STATION_SIMULATION_SUMMARY_TABLE_CONTENT_CONFIG: TableContentConfigProps<StationSimulationData>[] = [
    {
        id: "mapPackSummaryId",
        description: "Map Pack Summary ID",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.mapPackSummaryId} />
        ),
        sortingField: "mapPackSummaryId",
        filterGroup: "stationSimulationTableProp",
        filterOperators: TEXT_FIELD_OPERATORS,
    },
    {
        id: "stationId",
        description: "Station Codes",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.stationId} />
        ),
        sortingField: "stationId",
        filterGroup: "stationSimulationTableProp",
        filterOperators: TEXT_FIELD_OPERATORS,
    },
    {
        id: "originalSpr",
        description: "Original SPR",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.originalSpr)} />
        ),
        sortingField: "originalSpr",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "currentSpr",
        description: "Current SPR",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.currentSpr)} />
        ),
        sortingField: "currentSpr",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "nonNurserySprDelta",
        description: "Non-Nursery SPR Delta",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.nonNurserySprDelta)} />
        ),
        sortingField: "nonNurserySprDelta",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "originalDpph",
        description: "Original DPPH",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.originalDpph)} />
        ),
        sortingField: "originalDpph",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "currentDpph",
        description: "Current DPPH",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.currentDpph)} />
        ),
        sortingField: "currentDpph",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "dpphDelta",
        description: "DPPH Delta",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.dpphDelta)} />
        ),
        sortingField: "dpphDelta",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "originalTransitTime",
        description: "Original Transit Time",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.originalTransitTime)} />
        ),
        sortingField: "originalTransitTime",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "currentTransitTime",
        description: "Current Transit Time",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.currentTransitTime)} />
        ),
        sortingField: "currentTransitTime",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "transitTimeDelta",
        description: "Transit Time Delta",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.transitTimeDelta)} />
        ),
        sortingField: "transitTimeDelta",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "originalAvgTransitTime",
        description: "Original Average Transit Time",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.originalAvgTransitTime)} />
        ),
        sortingField: "originalAvgTransitTime",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "currentAvgTransitTime",
        description: "Current Average Transit Time",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.currentAvgTransitTime)} />
        ),
        sortingField: "currentAvgTransitTime",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "avgTransitTimeDelta",
        description: "Average Transit Time Delta",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.avgTransitTimeDelta)} />
        ),
        sortingField: "avgTransitTimeDelta",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "originalPackageCount",
        description: "Original Package Count",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.originalPackageCount} />
        ),
        sortingField: "originalPackageCount",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "currentPackageCount",
        description: "Current Package Count",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.currentPackageCount} />
        ),
        sortingField: "currentPackageCount",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "packageCountDelta",
        description: "Package Count Delta",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.packageCountDelta)} />
        ),
        sortingField: "packageCountDelta",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "originalStopCount",
        description: "Original Stop Count",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.originalStopCount} />
        ),
        sortingField: "originalStopCount",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "currentStopCount",
        description: "Current Stop Count",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.currentStopCount} />
        ),
        sortingField: "currentStopCount",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "stopCountDelta",
        description: "Stop Count Delta",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.stopCountDelta)} />
        ),
        sortingField: "stopCountDelta",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "originalRouteCount",
        description: "Original Route Count",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.originalRouteCount} />
        ),
        sortingField: "originalRouteCount",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "currentRouteCount",
        description: "Current Route Count",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={stationSimulationData.currentRouteCount} />
        ),
        sortingField: "currentRouteCount",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    },
    {
        id: "routeCountDelta",
        description: "Route Count Delta",
        tableCell: (stationSimulationData: StationSimulationData) => (
            <CopyTextField content={formatDecimal(stationSimulationData.routeCountDelta)} />
        ),
        sortingField: "routeCountDelta",
        filterGroup: "stationSimulationTableProp",
        filterOperators: NUMBER_FIELD_OPERATORS,
    }
];

export const STATION_SIMULATION_SUMMARY_TABLE_COLUMN_DEFINITIONS =
    configPropsToColumnDefinitions(STATION_SIMULATION_SUMMARY_TABLE_CONTENT_CONFIG);

export const STATION_SIMULATION_SUMMARY_TABLE_VISIBLE_CONTENT_OPTIONS: any = [
    {
        label: "Station Simulation Summary",
        options: columnDefinitionToOption(STATION_SIMULATION_SUMMARY_TABLE_COLUMN_DEFINITIONS),
    },
];

export const STATION_SIMULATION_SUMMARY_TABLE_FILTER_PROPERTIES: FilteringProperty[] =
    configPropsToFilterProps(STATION_SIMULATION_SUMMARY_TABLE_CONTENT_CONFIG).sort((a, b) =>
        a.propertyLabel.localeCompare(b.propertyLabel)
    );

export const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
    { value: 25, label: "25 Entries" },
    { value: 50, label: "50 Entries" },
    { value: 100, label: "100 Entries" },
];